import { useAuftragRepository } from '@Hooks/useRepository';
import { type ApiContainerBilledAsOffer, type ApiOrder } from '@Model';
import { ContainerActionBilledAsRepository } from '@Repositories/Auftragsbearbeitung';
import {
    type ApiActionOption,
    type ApiMessageAction,
    type AsyncStateUpdate,
    EnumMessageAction,
    isOk,
    useActionMessage,
    useToasts,
} from '@synqo/frontend-component-library';
import { useCallback } from 'react';

const RESOURCE_TYPE = 'container_billed_as';

const offerOf = (value: string) => JSON.parse(value) as ApiContainerBilledAsOffer;

const leaveToTheNotification = () => undefined;

/**
 * Restores the billed-as container type of a container action once the user accepts the offer in its notification.
 *
 * @param orderId The order being edited; offers for other orders are left to their own frames.
 * @param requestUpdateState Takes the changed action into the order.
 */
// @ai-generated 2026-10-28T15:30:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function useContainerBilledAsOffer(orderId: string, requestUpdateState: AsyncStateUpdate<ApiOrder>): void {
    const billedAs = useAuftragRepository(ContainerActionBilledAsRepository);
    const { pushAllToasts } = useToasts();

    const canHandle = useCallback(
        (message: ApiMessageAction) =>
            message.action === EnumMessageAction.LoadRelatedData &&
            message.resource_type === RESOURCE_TYPE &&
            offerOf(message.options[0].value).order_id === orderId,
        [orderId],
    );

    const restoreBilledAs = useCallback(
        async (option: ApiActionOption) => {
            const offer = offerOf(option.value);
            const result = await billedAs.addLinkedObject(
                offer.billed_as.foreign_key as string,
                offer.container_action_id,
                offer.order_id,
            );

            pushAllToasts(result.messages);
            if (!isOk(result)) {
                return;
            }

            await requestUpdateState((latest) => ({
                data: {
                    ...latest,
                    container_actions: latest.container_actions.map((action) =>
                        action.id === result.data.id ? result.data : action,
                    ),
                },
                messages: [],
            }));
        },
        [billedAs, pushAllToasts, requestUpdateState],
    );

    const handleOption = useCallback((option: ApiActionOption) => void restoreBilledAs(option), [restoreBilledAs]);

    useActionMessage({ canHandle, handleMessage: leaveToTheNotification, handleOption });
}
